/**
 * `/admin/automation/slots` (A7Y-3600) — live state of every UKKO runner
 * slot. Wiring only: this component owns the fetch and renders the
 * presentational sections from `SlotsSections.tsx` in the ticket's specified
 * order. Loading/error states cloned from `AddisStatusBoard`'s.
 *
 * Guard order is `isError` then `!data` — not `isLoading` — so a background
 * refetch error still surfaces and a stale render survives rather than being
 * replaced by a loading spinner every 60s.
 *
 * READ-ONLY. No cancel, restart or drain control, and no button that would
 * trigger one.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtLocal, fmtUtc, useNow } from './shared';
import { groupSlots } from './slots-display';
import { BannerRule } from './WorkloadsSections';
import { QueueLines, SlotGroup, SlotsFindings, SlotsLegend, UnreadableNote } from './SlotsSections';

const REFETCH_INTERVAL_MS = 60_000;

export default function SlotsPage() {
  const query = useQuery({
    queryKey: ['ukko-slots'],
    queryFn: () => api.getUkkoSlots(),
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  const nowMs = useNow(1_000);

  if (query.isError) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Slots failed to load. Check the Worker logs for /admin/autobuild/slots.
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-zinc-500">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Reading slot status…
      </div>
    );
  }

  const status = query.data;
  const grouped = groupSlots(status.slots);

  return (
    <div className="space-y-4">
      <BannerRule>
        UKKO slots ·{' '}
        <span title={fmtUtc(status.generatedAt)}>{fmtLocal(status.generatedAt)}</span>
      </BannerRule>

      <SlotGroup slotClass="session" slots={grouped.session} nowMs={nowMs} />
      <SlotGroup slotClass="verify" slots={grouped.verify} nowMs={nowMs} />
      <SlotGroup slotClass="deploy" slots={grouped.deploy} nowMs={nowMs} />

      <QueueLines queues={status.queues} />
      <SlotsFindings findings={status.findings} />
      <SlotsLegend slotCount={status.slots.length} />
      <UnreadableNote unreadable={status.unreadable} />
    </div>
  );
}
